/**
 * PDF.js loader.
 *
 * pdf.js is roughly 400 kB, and most routes never render a document, so the
 * library is imported dynamically the first time a page is drawn. Pages render
 * to a canvas sized by CSS width with the backing store scaled by
 * devicePixelRatio, so text stays sharp on phones without inflating layout.
 */

import { API_CREDENTIALS } from './api';
type PdfModule = typeof import('pdfjs-dist');
export type LoadedPdf = import('pdfjs-dist').PDFDocumentProxy;

let modulePromise: Promise<PdfModule> | null = null;

function pdfjs(): Promise<PdfModule> {
  if (!modulePromise) {
    modulePromise = (async () => {
      const [lib, worker] = await Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
      ]);
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    })();
  }
  return modulePromise;
}

const cache = new Map<string, Promise<LoadedPdf>>();

/**
 * Load a document, optionally with request headers.
 *
 * pdf.js cannot attach custom headers to its own range requests, and the
 * recipient signing session travels in a header rather than the URL (a token in
 * a URL leaks through history, logs and referrers). So when headers are needed
 * we fetch the bytes ourselves and hand pdf.js the buffer.
 */
export function loadPdf(url: string, headers?: Record<string, string>): Promise<LoadedPdf> {
  const key = headers ? `${url}::auth` : url;
  let existing = cache.get(key);
  if (!existing) {
    existing = (async () => {
      const lib = await pdfjs();
      if (!headers || Object.keys(headers).length === 0) {
        return lib.getDocument({ url, withCredentials: true }).promise;
      }
      // Same credential rule as the API client: cookies travel only to the
      // origin this deployment was built to talk to.
      const response = await fetch(url, { headers, credentials: API_CREDENTIALS });
      if (!response.ok) throw new Error(`document fetch failed (${response.status})`);
      const data = new Uint8Array(await response.arrayBuffer());
      return lib.getDocument({ data }).promise;
    })();
    cache.set(key, existing);
  }
  return existing;
}

export function clearPdfCache(url?: string) {
  if (url) cache.delete(url);
  else cache.clear();
}

export async function renderPageToCanvas(
  pdf: LoadedPdf,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  cssWidth: number,
) {
  const page = await pdf.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: cssWidth / base.width });
  // Cap the pixel ratio: a 3x backing store on a large page is slow on phones.
  const ratio = Math.min(window.devicePixelRatio || 1, 2);

  canvas.width = Math.floor(viewport.width * ratio);
  canvas.height = Math.floor(viewport.height * ratio);
  canvas.style.width = '100%';
  canvas.style.aspectRatio = `${base.width} / ${base.height}`;

  const context = canvas.getContext('2d');
  if (!context) return;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  await page.render({ canvasContext: context, viewport }).promise;
}
